import { useState } from 'react'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { euiIconTypeMap } from '../../../components/icons'
import { DemandRequestLineItemStatus } from '../../../graphql'
import { canCancelLineItem } from '../utils/demandRequestStatus'

const MoreActionsIcon = euiIconTypeMap.boxesHorizontal
const DocumentEditIcon = euiIconTypeMap.documentEdit
const CheckIcon = euiIconTypeMap.check
const RefreshIcon = euiIconTypeMap.refresh
const CrossIcon = euiIconTypeMap.cross

type LineItemActionsMenuProps = {
	lineItemId: string,
	status?: DemandRequestLineItemStatus | null,
	fulfillerNotes?: string | null,
	onCompleteLineItem: ( lineItemId: string ) => void,
	onReopenLineItem: ( lineItemId: string ) => void,
	onCancelLineItem: ( lineItemId: string ) => void,
	onEditFulfillerNote: ( lineItemId: string ) => void,
}

export default function LineItemActionsMenu( { lineItemId, status, fulfillerNotes, onCompleteLineItem, onReopenLineItem, onCancelLineItem, onEditFulfillerNote }: LineItemActionsMenuProps ) {
	const [ anchorEl, setAnchorEl ] = useState<null | HTMLElement>( null )
	const isOpen = Boolean( anchorEl )

	const closeMenu = () => setAnchorEl( null )

	const isCompleted = status === DemandRequestLineItemStatus.Completed
	const isCancelled = status === DemandRequestLineItemStatus.Cancelled
	const canCancel = canCancelLineItem( status )

	const hasFulfillerNote = !!fulfillerNotes?.trim()

	const menuItems = [
		{
			name: hasFulfillerNote ? 'Edit fulfillment note' : 'Add fulfillment note',
			icon: DocumentEditIcon,
			onClick: () => {
				closeMenu()
				onEditFulfillerNote( lineItemId )
			},
		},
	]

	if ( !isCompleted && !isCancelled ) {
		menuItems.push( {
			name: 'Mark as Completed',
			icon: CheckIcon,
			onClick: () => {
				closeMenu()
				onCompleteLineItem( lineItemId )
			},
		} )
	}

	if ( isCompleted || isCancelled ) {
		menuItems.push( {
			name: 'Reopen',
			icon: RefreshIcon,
			onClick: () => {
				closeMenu()
				onReopenLineItem( lineItemId )
			},
		} )
	}

	if ( canCancel ) {
		menuItems.push( {
			name: 'Cancel',
			icon: CrossIcon,
			onClick: () => {
				closeMenu()
				onCancelLineItem( lineItemId )
			},
		} )
	}

	return (
		<>
			<IconButton
				aria-label="Actions"
				onClick={( event ) => setAnchorEl( event.currentTarget )}
			>
				<MoreActionsIcon />
			</IconButton>
			<Menu anchorEl={anchorEl} open={isOpen} onClose={closeMenu}>
				{menuItems.map( ( item ) => (
					<MenuItem key={item.name} onClick={item.onClick}>
						<ListItemIcon>
							<item.icon fontSize="small" />
						</ListItemIcon>
						<ListItemText>{item.name}</ListItemText>
					</MenuItem>
				) )}
			</Menu>
		</>
	)
}
